import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true); // true while checking session


  // ─────────────────────────────────────
  // AUTH INIT FLOW (APP BOOT)
  // ─────────────────────────────────────
  // 1. Check existing session from Supabase
  // 2. Set user if session exists
  // 3. Stop loading state
  useEffect(() => {
    // 1. Get current session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });


    // 2. Listen for login / logout / token refresh events
    // ─────────────────────────────────────
    // AUTH REALTIME SYNC FLOW
    // ─────────────────────────────────────
    // 1. Listen for login / logout / refresh
    // 2. Update user state instantly
    // 3. Keep UI in sync with Supabase session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // ── Auth actions ──────────────────────────────────────────

  // ─────────────────────────────────────
  // SIGN UP FLOW
  // ─────────────────────────────────────
  // 1. Send email + password to Supabase
  // 2. Include captcha verification token
  // 3. Return success/error response
  const signUp = async (email, password, captchaToken) => {
    const { data, error } = await supabase.auth.signUp({ email, password, options : {captchaToken}});
    return { data, error };
  };

  // ─────────────────────────────────────
  // SIGN IN FLOW
  // ─────────────────────────────────────
  // 1. Validate credentials with Supabase
  // 2. Create session if valid
  // 3. Trigger auth state listener automatically
  const signIn = async (email, password, captchaToken) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password, options : {captchaToken} });
    return { data, error };
  };

  // ─────────────────────────────────────
  // SIGN OUT FLOW
  // ─────────────────────────────────────
  // 1. Clear Supabase session
  // 2. User becomes null automatically via listener
  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const signInWithGoogle = async () => {

  const { data, error } =
    await supabase.auth.signInWithOAuth({

      provider: "google",

      options: {

        redirectTo:
          window.location.origin + "/chat",
          // redirectTo:
          // 'http://localhost:3000' + "/chat",
      },
    });

  return { data, error };
};

  return (
    // ─────────────────────────────────────
    // GLOBAL AUTH PROVIDER EXPORT FLOW
    // ─────────────────────────────────────
    // Makes auth state + actions available app-wide
    <AuthContext.Provider value={{ user, loading, signUp, signIn, signOut, signInWithGoogle}}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook — use this anywhere in the app

// ─────────────────────────────────────
// AUTH HOOK FLOW
// ─────────────────────────────────────
// 1. Access global auth context
// 2. Throw error if used outside provider
// 3. Return user + auth functions
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
